import type { ConsumerApiClient } from '../client';

// Backend returns plain string arrays for jurisdictions, states, and cities
// e.g. ["Federal", "Local", "State"], ["CA", "NY"], ["Los Angeles", "San Diego"]

export interface Facility {
  id: string;
  name: string;
  type: string;
  jurisdiction: string;
  city: string;
  state: string;
  address?: string;
  partnership_type: string;
  rules?: string;
}

export interface InmateSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  inmateNumber: string;
  facilityId: string;
  facilityName: string;
}

export interface ContentRule {
  id: string;
  name: string;
  title?: string;
  ruleType: string;
  category?: string;
  description?: string;
  explainerText?: string;
  headerText?: string;
  icon?: string;
  parameters?: Record<string, any>;
  explainerImages?: string[];
  status: 'active' | 'inactive';
}

export interface FacilityAttribute {
  id: string;
  facilityId: string;
  attributeName: string;
  attributeLabel: string;
  isRequired: boolean;
  clientAssistUrl?: string | null;
  sortOrder: number;
}

export interface SearchFacilitiesParams {
  jurisdiction?: string;
  state?: string;
  city?: string;
  search?: string;
}

export interface SearchInmatesParams {
  name?: string;
  inmateNumber?: string;
}

export interface FacilitiesApi {
  getJurisdictions(): Promise<string[]>;
  getStates(jurisdiction: string): Promise<string[]>;
  getCities(state: string, jurisdiction: string): Promise<string[]>;
  searchFacilities(params: SearchFacilitiesParams): Promise<Facility[]>;
  getFacility(id: string): Promise<Facility>;
  getFacilityRules(id: string): Promise<{ rules: string; facilityName: string }>;
  searchInmates(facilityId: string, params: SearchInmatesParams): Promise<InmateSearchResult[]>;
  getContentRules(facilityId: string): Promise<ContentRule[]>;
  getFacilityAttributes(facilityId: string): Promise<FacilityAttribute[]>;
}

export function createFacilitiesApi(client: ConsumerApiClient): FacilitiesApi {
  return {
    getJurisdictions() {
      return client.get('/facilities/jurisdictions', { skipAuth: true });
    },

    getStates(jurisdiction) {
      return client.get(
        `/facilities/states?jurisdiction=${encodeURIComponent(jurisdiction)}`,
        { skipAuth: true },
      );
    },

    getCities(state, jurisdiction) {
      return client.get(
        `/facilities/cities?state=${encodeURIComponent(state)}&jurisdiction=${encodeURIComponent(jurisdiction)}`,
        { skipAuth: true },
      );
    },

    searchFacilities(params) {
      const query = new URLSearchParams();
      if (params.jurisdiction) query.set('jurisdiction', params.jurisdiction);
      if (params.state) query.set('state', params.state);
      if (params.city) query.set('city', params.city);
      if (params.search) query.set('search', params.search);
      return client.get(`/facilities/search?${query.toString()}`, { skipAuth: true });
    },

    getFacility(id) {
      return client.get(`/facilities/${id}`, { skipAuth: true });
    },

    getFacilityRules(id) {
      return client.get(`/facilities/${id}/rules`, { skipAuth: true });
    },

    searchInmates(facilityId, params) {
      const search = params.name || params.inmateNumber || '';
      const query = new URLSearchParams();
      if (search) query.set('search', search);
      return client.get(`/facilities/${facilityId}/inmates?${query.toString()}`, {
        skipAuth: true,
      });
    },

    getContentRules(facilityId) {
      return client.get(`/facilities/${facilityId}/content-rules`);
    },

    getFacilityAttributes(facilityId) {
      return client.get(`/facilities/${facilityId}/attributes`);
    },
  };
}
